import { PageLayout } from '@repo/ui/components/page-layout'
import {
	makeBreadcrumbFromCache,
	QueryBreadcrumbs
} from '@repo/ui/components/query-breadcrumbs'
import { Separator } from '@repo/ui/separator'
import { Skeleton } from '@repo/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { getErrorCode } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { z } from 'zod'
import { translateKey, useTranslation } from '@/lib/i18n'
import { groupDetailQueryOptions } from '@/modules/groups/query/query-options'
import { makeQueryKeys } from '@/query/query-keys'
import { AddMembersDialog } from '@/routes/(private)/(shell)/groups/$groupId/-components/add-members-dialog'
import { GroupHeader } from '@/routes/(private)/(shell)/groups/$groupId/-components/group-header'
import { GroupNotFound } from '@/routes/(private)/(shell)/groups/$groupId/-components/group-not-found'
import { MembersTab } from '@/routes/(private)/(shell)/groups/$groupId/-components/members-tab'
import { SettingsTab } from '@/routes/(private)/(shell)/groups/$groupId/-components/settings-tab'

const searchSchema = z.object({
	tab: z.enum(['members', 'settings']).default('members'),
	// The members search, matched by the server on name and email.
	search: z.string().trim().default(''),
	add: z.boolean().default(false),
	// Set by the new-group flow only: the dialog then reads «just created».
	created: z.boolean().default(false)
})

export const Route = createFileRoute('/(private)/(shell)/groups/$groupId/')({
	component: Group,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache(
				queryClient,
				makeQueryKeys.groups.detail(params.groupId),
				(group: { name: string }) => group.name
			)
	}
})

/**
 * One group, for anyone who can see it. Whoever may change it (`canManage`)
 * gets the tabs «Membri» and «Impostazioni»; everyone else gets the header and
 * the members, with no tab bar. The tab and the «Aggiungi membri» dialog are in
 * the URL, so the list's row menu and the dialog opened after creating a group
 * can deep-link here; without `canManage` both are ignored, `?tab=settings`
 * falling back to the members.
 *
 * `add=true` opens the dialog, in its «just created» form when `created=true`
 * came with it (the new-group flow); closing it drops both by replacing the
 * entry, so Back does not reopen it.
 *
 * A 404 is one of two states: «non esiste più» when this page had read the
 * group before (it was deleted, or went private to the caller), «non è
 * disponibile» when the first read already refused.
 */
function Group() {
	const { t } = useTranslation()
	const { groupId } = Route.useParams()
	const { tab, add, created } = Route.useSearch()
	const navigate = useNavigate()
	const query = useQuery(groupDetailQueryOptions(groupId))
	const group = query.data

	if (query.isPending) {
		return (
			<PageLayout breadcrumb={<QueryBreadcrumbs />}>
				<div className='grid gap-4 px-4 pt-4 md:px-6'>
					<Skeleton className='h-11 w-72' />
					<Skeleton className='h-9 w-56' />
					<Skeleton className='h-40 w-full' />
				</div>
			</PageLayout>
		)
	}

	// Decided on the error before the data: a refetch that fails keeps the old
	// group in `data`, and a group deleted meanwhile must not be shown live.
	if (group === undefined || getErrorCode(query.error) === 'not_found') {
		// Every refusal for a group the caller cannot open is a 404 by design.
		return (
			<PageLayout breadcrumb={<QueryBreadcrumbs />}>
				{getErrorCode(query.error) === 'not_found' ? (
					<GroupNotFound
						variant={group === undefined ? 'unavailable' : 'gone'}
					/>
				) : (
					<p className='px-4 text-destructive text-sm md:px-6'>
						{translateKey('shared:http_errors.default')}
					</p>
				)}
			</PageLayout>
		)
	}

	return (
		<PageLayout breadcrumb={<QueryBreadcrumbs />}>
			<div className='flex min-h-0 flex-1 flex-col gap-6 px-4 pt-4 pb-6 md:px-6'>
				<GroupHeader group={group} />

				{group.canManage ? (
					<Tabs
						className='min-h-0 flex-1'
						onValueChange={(next) => {
							navigate({
								to: '.',
								search: (prev) => ({
									...prev,
									tab:
										next === 'settings'
											? 'settings'
											: 'members'
								})
							})
						}}
						value={tab}
					>
						<div>
							<TabsList variant='line'>
								<TabsTrigger value='members'>
									{t('groups:detail.tab_members')}
								</TabsTrigger>
								<TabsTrigger value='settings'>
									{t('groups:detail.tab_settings')}
								</TabsTrigger>
							</TabsList>
							<Separator />
						</div>
						<TabsContent
							className='flex min-h-0 flex-1 flex-col pt-4'
							value='members'
						>
							<MembersTab group={group} />
						</TabsContent>
						<TabsContent
							className='min-h-0 flex-1 overflow-y-auto'
							value='settings'
						>
							<SettingsTab group={group} />
						</TabsContent>
					</Tabs>
				) : (
					<MembersTab group={group} />
				)}
			</div>

			<AddMembersDialog
				groupId={group.id}
				groupName={group.name}
				onOpenChange={(open) => {
					if (!open) {
						navigate({
							to: '.',
							replace: true,
							search: (prev) => ({
								...prev,
								add: false,
								created: false
							})
						})
					}
				}}
				open={add && group.canManage}
				variant={created ? 'created' : 'default'}
			/>
		</PageLayout>
	)
}
